import type {
  AcceptedBotDefeatOutcome,
  BotDefeatRuntime,
} from "./bot-defeat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerFixedStepListener } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import type { Vector2 } from "../simulation/player-movement";

export interface CoinRewardFeedback {
  readonly coins: number;
  readonly position: Vector2;
  readonly revision: number;
  readonly startedAtMs: number;
}

export interface CoinRewardFeedbackSnapshot {
  readonly feedback: CoinRewardFeedback | null;
}

export type CoinRewardFeedbackListener = (
  snapshot: CoinRewardFeedbackSnapshot,
) => void;

export interface CoinRewardFeedbackPort {
  getSnapshot(): CoinRewardFeedbackSnapshot;
}

type DefeatSource = Pick<BotDefeatRuntime, "subscribe">;
interface PlayerTimeSource {
  getSnapshot(): { readonly simulationTimeMs: number };
  subscribeFixedStep(listener: PlayerFixedStepListener): () => void;
}

export const formatCoinRewardFeedback = (
  feedback: CoinRewardFeedback,
): string => `+${String(feedback.coins)} coins`;

export class CoinRewardFeedbackRuntime implements CoinRewardFeedbackPort {
  private destroyed = false;
  private feedback: CoinRewardFeedback | null = null;
  private readonly listeners = new Set<CoinRewardFeedbackListener>();
  private revision = 0;
  private readonly unsubscribers: readonly (() => void)[];

  public constructor(
    private readonly flow: Pick<GameFlow, "getSnapshot" | "subscribe">,
    defeats: DefeatSource,
    player: PlayerTimeSource,
  ) {
    this.unsubscribers = [
      this.flow.subscribe((snapshot) => {
        this.applyFlow(snapshot);
      }),
      defeats.subscribe((outcome) => {
        this.accept(outcome, player.getSnapshot().simulationTimeMs);
      }),
      player.subscribeFixedStep((_stepMilliseconds, simulationTimeMs) => {
        void _stepMilliseconds;
        this.expire(simulationTimeMs);
      }),
    ];
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.listeners.clear();
    this.feedback = null;
  }

  public getSnapshot(): CoinRewardFeedbackSnapshot {
    return {
      feedback:
        this.feedback === null
          ? null
          : { ...this.feedback, position: { ...this.feedback.position } },
    };
  }

  public subscribe(listener: CoinRewardFeedbackListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private accept(outcome: AcceptedBotDefeatOutcome, startedAtMs: number): void {
    const flow = this.flow.getSnapshot();
    if (
      flow.scene !== "gameplay" ||
      outcome.winnerId !== "player" ||
      outcome.rewardCoins <= 0
    ) {
      return;
    }
    this.revision += 1;
    this.feedback = {
      coins: outcome.rewardCoins,
      position: { ...outcome.dropPosition },
      revision: this.revision,
      startedAtMs,
    };
    this.notify();
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene !== "gameplay") {
      this.clear();
    }
  }

  private clear(): void {
    if (this.feedback === null) {
      return;
    }
    this.feedback = null;
    this.notify();
  }

  private expire(simulationTimeMs: number): void {
    if (
      this.feedback !== null &&
      simulationTimeMs - this.feedback.startedAtMs >=
        gameplayConfig.playerStatus.feedbackDurationMs
    ) {
      this.clear();
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }
}
